import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ViewWillEnter, ViewWillLeave, IonContent } from '@ionic/angular';
import { PanAppState, PlatformService, Tracking } from '@panamax/app-state';
import { DealsAnalyticsService } from '@usf/ngrx-promotions';
import { Subscription, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { DealsService } from './services/deals.service';
import { TranslateService } from '@ngx-translate/core';
import { QuantityUpdate } from '@shared/models/quantity-update';
import { OrderItemService } from '@shared/services/order-item.service';
import { MerchandisingProductViewModel } from '@shared/models/merchandising-product-view.model';
import { PdpLnkSrc } from '@shared/constants/pdp-lnksrc.enum';
import { WIN_BACKS_FEATURE } from '@shared/constants/merchandising-constants';
import { MerchandisingProductCarouselComponent } from '@app/shared-product/components/merchandising-product-carousel/merchandising-product-carousel.component';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { FEATURES } from '@shared/constants/splitio-features';

@Component({
  selector: 'app-deals',
  templateUrl: './deals.page.html',
  styleUrls: ['./deals.page.scss'],
})
export class DealsPage implements ViewWillEnter, ViewWillLeave {
  @ViewChild(MerchandisingProductCarouselComponent)
  carousel: MerchandisingProductCarouselComponent;
  @ViewChild(IonContent) content: IonContent;

  breadcrumbs: Crumb[];
  dealsProductExistSub$: Subscription;
  deepLinkCustomer$: Subscription;
  revManWinbacksSub: Subscription;
  revManWinbacks: MerchandisingProductViewModel[];
  excessObsolete$: Observable<MerchandisingProductViewModel[]>;
  showAllDealsBanner: boolean;
  allDealsPath = [PATHS.DEALS, PATHS.ALL_DEALS];
  whileSuppliesCode = [environment.whileSuppliesLast.toString()];
  newScoopSearchKey = [environment.allScoop];
  backgroundImageStyle = "url('/assets/images/deals-hero.webp')";
  overlayGradient =
    'linear-gradient(90deg, rgba(47,47,47,0.77) 0%, rgba(47,47,47,0.77) 8%, rgba(47,47,47,0) 50%, rgba(47,47,47,0.77) 91%, rgba(47,47,47,0.77) 100%)';
  revManLnkSrc: PdpLnkSrc = PdpLnkSrc.revMan;

  WIN_BACKS_FEATURE = WIN_BACKS_FEATURE;
  isNewSearchEnabled$: Observable<boolean>;
  excessObsoleteFlag = FEATURES.split_dynamic_excess_obsolete.name;

  constructor(
    readonly dealsService: DealsService,
    readonly platformService: PlatformService,
    readonly dealsAnalyticsService: DealsAnalyticsService,
    readonly productAnalyticsService: ProductAnalyticsService,
    readonly router: Router,
    private route: ActivatedRoute,
    private orderItemService: OrderItemService,
    private panAppState: PanAppState,
    private translateService: TranslateService,
    private toggleNewSearchService: ToggleNewSearchService,
  ) {}

  ionViewWillEnter(): void {
    this.isNewSearchEnabled$ =
      this.toggleNewSearchService.newSearchEnabledObservable$;
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.deals),
    );
    this.route.queryParams.pipe(take(1)).subscribe(async params => {
      this.dealsProductExistSub$ = this.dealsService
        .selectDealsProductsExist$()
        .subscribe(dealsProductsExist => {
          this.showAllDealsBanner = dealsProductsExist;
          if (this.showAllDealsBanner && params.customerNumber) {
            this.router.navigate(this.allDealsPath, {
              queryParams: params,
            });
          }
        });
    });

    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url:
          this.platformService.platformType ===
          this.platformService.platformEnum.desktop
            ? '/desktop/home'
            : '/touch/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.deals',
        url: '',
        active: true,
      },
    ];
    this.dealsAnalyticsService.trackPageLoad();

    this.revManWinbacksSub = this.dealsService
      .getRevManWinbacks$()
      .subscribe(revManWinbacks => {
        this.revManWinbacks = revManWinbacks;
      });

    this.excessObsolete$ = this.dealsService.getExcessObsoleteProducts$();

    if (this.content) {
      this.content.scrollToTop();
    }
  }

  ionViewWillLeave() {
    if (this.dealsProductExistSub$) {
      this.dealsProductExistSub$.unsubscribe();
    }
    this.revManWinbacksSub?.unsubscribe();
  }

  onRevManWinbackQuantityUpdate(quantityUpdate: QuantityUpdate, index: number) {
    const trackingData: Tracking = {
      analytics: {
        data: {
          products: [
            {
              addMethod: WIN_BACKS_FEATURE,
              merchFeatureName: WIN_BACKS_FEATURE,
            },
          ],
          results: {
            resultRank: index + 1,
          },
        },
      },
    };
    this.orderItemService.changeQuantity(
      quantityUpdate.productNumber,
      quantityUpdate.quantity,
      quantityUpdate.isEaches,
      trackingData,
    );
  }

  onExcessObsoleteQuantityUpdate(quantityUpdate: QuantityUpdate) {
    this.orderItemService.changeQuantity(
      quantityUpdate.productNumber,
      quantityUpdate.quantity,
      quantityUpdate.isEaches,
    );
  }

  onRevManWinbacksView() {
    const visibleRevmanWinbackIndexes = new Set([
      ...(!!this.carousel?.currentIntersectingIndexSet
        ? this.carousel.currentIntersectingIndexSet
        : []),
    ]);
    const products = this.revManWinbacks
      ?.filter((_, index) => visibleRevmanWinbackIndexes.has(index))
      .map(product => ({
        divisionNumber: product?.summary?.divisionNumber,
        productNumber: product?.productNumber,
        attributes: product?.trackingAttributes,
      }));

    if (products?.length > 0) {
      this.productAnalyticsService.trackRevManWinbacksView(products);
    }
  }
}
